import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { Input, Select } from '@/components/Form'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import { getMyOfferTableColumns, myOfferTableHeaders } from '@/features/offers/routes/MyOfferPage/myOfferTableColumns'
import { useReason } from '@/features/reasons/useReason'
import useVendor from '@/features/vendors/useVendor'
import { SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import {
  myOfferDisplayLevelOptions,
  mySourcingOfferDisplayLevelOptions,
  tableTypeOptions,
} from '@/features/tableProfiles/constants/tableProfile.constant'
import { convertToTableDetails, getTableColumns } from '@/features/tableProfiles/helpers/tableProfile.helper'
import TableProfileColumnItem from '@/features/tableProfiles/routes/EditTableProfilePage/TableProfileColumnItem'
import { TableType } from '@/features/tableProfiles/tableProfile.enum'
import useTableProfile from '@/features/tableProfiles/useTableProfile'
import useUser from '@/features/users/useUser'
import { authSelector } from '@/stores/auth/authSlice'
import { toast } from 'react-toastify'
import { LoadingSection } from '@/components/Elements'
import { getMySourcingTableColumns } from '@/features/offers/routes/MySourcingPage/mySourcingTableColumns'

//API

//Constant

const EditTableProfilePage = () => {
  //State
  const { id } = useParams()
  const { user: currentUser } = useSelector(authSelector)

  const [tableColumns, setTableColumns] = useState([])
  const [displayLevelOptions, setDisplayLevelOptions] = useState([])

  const { useFetchTableProfile, useUpdateTableProfile } = useTableProfile()
  const updateTableProfile = useUpdateTableProfile()
  const { data: fetchProfileResponse, isLoading } = useFetchTableProfile(id)

  const methods = useForm()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
  const { useFetchAllUsers, useUserOptions } = useUser()
  const { useVendorOptions } = useVendor()

  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useSystemVariableOptions } = useSystemVariable()
  const { useFetchReasonsByType } = useReason()

  const { data: salesUserOptions } = useUserOptions({
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })
  const { data: engineerUserOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, ContributorGroupType.ENGINEER],
  })
  const { data: allUserOptions } = useUserOptions()
  const { data: PMOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.TECHNICAL_PROJECT_MANAGER, ContributorGroupType.SALES],
  })
  const { data: teamAssistantOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.TEAM_ASSISTANT],
  })

  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, { status: 'active' })

  const { data: costCenterOptions } = useCostCenterOptions({ status: 'active', labelType: 'simple' })
  const { data: GCKOptions } = useGCKCodeOptions()

  const { data: SISICOptions } = useSystemVariableOptions(SystemVariableType.SISIC, {
    status: 'active',
    valueKey: 'code',
    labelKey: (sisic) => `${sisic.code} - ${sisic.name}`,
  })

  const { data: CPMOptions, isLoading: isLoadingCPMOptions } = useUserOptions({
    contributorGroup: [ContributorGroupType.COMMERCIAL_PROJECT_MANAGER],
  })

  const { data: vendorOptions } = useVendorOptions({ status: 'active', valueKey: 'name' })
  const { data: sourcingUserOptions } = useUserOptions({
    status: 'active',
    contributorGroup: ContributorGroupType.PROCUREMENT_OFFICER,
  })

  const { data: fetchReasonResponse } = useFetchReasonsByType('offer')

  const allReasons = fetchReasonResponse?.results ?? []

  useEffect(() => {
    const profile = fetchProfileResponse?.data
    if (profile) {
      methods.reset(profile)
    }

    if (
      profile?.tableDetails &&
      profile.tableType &&
      salesUserOptions &&
      engineerUserOptions &&
      allUserOptions &&
      teamOptions &&
      businessUnitOptions &&
      PMOptions &&
      costCenterOptions &&
      GCKOptions &&
      allReasons &&
      SISICOptions &&
      CPMOptions &&
      teamAssistantOptions &&
      sourcingUserOptions &&
      vendorOptions
    ) {
      //
      let columns = []
      let headers = {}
      switch (profile.tableType) {
        case TableType.OFFERS_LIST:
          columns = getMyOfferTableColumns({
            salesUserOptions,
            engineerUserOptions,
            creatorOptions: allUserOptions,
            teamOptions,
            businessUnitOptions,
            PMOptions,
            costCenterOptions,
            GCKOptions,
            closeReasons: allReasons.map((reason) => reason.name),
            SISICOptions,
            teamAssistantOptions,
            CPMOptions,
            sourcingUserOptions,
            vendorOptions,
          })
          headers = myOfferTableHeaders
          setDisplayLevelOptions(myOfferDisplayLevelOptions)
          break
        case TableType.SOURCING_OFFER_LIST:
          columns = getMySourcingTableColumns({
            sourcingUserOptions,
            vendorOptions,
            creatorOptions: allUserOptions,
            salesUserOptions,
          })
          setDisplayLevelOptions(mySourcingOfferDisplayLevelOptions)
          break
      }
      const profileTableColumns = getTableColumns(profile?.tableDetails, columns, headers)
      setTableColumns(profileTableColumns)
    }
  }, [
    fetchProfileResponse?.data,
    salesUserOptions?.length,
    engineerUserOptions?.length,
    allUserOptions?.length,
    teamOptions?.length,
    businessUnitOptions?.length,
    PMOptions?.length,
    costCenterOptions?.length,
    GCKOptions?.length,
    allReasons?.length,
    SISICOptions?.length,
    CPMOptions?.length,
    teamAssistantOptions?.length,
    sourcingUserOptions?.length,
    vendorOptions?.length,
  ])

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id === over.id) {
      z
      return
    }
    setTableColumns((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id)
      const newIndex = items.findIndex((item) => item.id === over.id)

      return arrayMove(items, oldIndex, newIndex)
    })
  }

  const handleUpdateColumn = (id, newColumn) => {
    setTableColumns((prevColumns) => prevColumns.map((col) => (col.id === id ? newColumn : col)))
  }

  const onSubmit = async (data) => {
    try {
      const res = await updateTableProfile.mutateAsync({
        id: id,
        data: {
          tableType: data.tableType,
          profileName: data.profileName,
          status: data.status,
          tableDetails: convertToTableDetails({ tableColumns, haveActionButton: true }),
          displayLevel: data.displayLevel,
        },
      })
    } catch (err) {
      toast.error(err?.message ?? '')
    }
  }

  const handleShowAllColumns = () => {
    setTableColumns((prevTableColumns) =>
      prevTableColumns.map((column) => {
        return {
          ...column,
          show: true,
        }
      })
    )
  }

  const handleHideAllColumns = () => {
    setTableColumns((prevTableColumns) =>
      prevTableColumns.map((column) => {
        return {
          ...column,
          show: false,
        }
      })
    )
  }

  if (isLoading || !fetchProfileResponse?.data) {
    return <LoadingSection />
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} autoComplete="off">
          <Flex p={5} borderBottom="1px solid #d8dbe0">
            <Heading>Table Profile Detail</Heading>
          </Flex>
          <Box p={5}>
            <Flex gap={3} direction={'column'}>
              <Input name="profileName" label="Profile Name" isRow isRequired />
              <Select name="tableType" label="Table Type" options={tableTypeOptions ?? []} isRow disabled />
              <Select name="displayLevel" label="Display Level" options={displayLevelOptions ?? []} isRow />
              <Flex gap="3">
                <Heading size={'md'}>Profile Columns</Heading>
                <Spacer />
                <Button colorScheme="teal" onClick={handleShowAllColumns}>
                  Show All
                </Button>
                <Button onClick={handleHideAllColumns}>Hide All</Button>
              </Flex>
            </Flex>

            <Flex direction={'column'} gap="3" overflowY={'scroll'} h="800px" mt="5">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={tableColumns} strategy={verticalListSortingStrategy}>
                  {tableColumns.map((column) => (
                    <TableProfileColumnItem key={column.id} column={column} handleUpdateColumn={handleUpdateColumn} />
                  ))}
                </SortableContext>
              </DndContext>
            </Flex>

            <Button mt="6" colorScheme="blue" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}

export default EditTableProfilePage
